import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
    CostCentreDto,
    ManufactureProcessBreakdownDto,
    ManufactureProcessCategoryBreakdownDto,
    ManufactureProcessColumnDefinitionDto,
    ManufactureProcessDto,
} from 'app/api/models';
import { ManufactureProcessService } from 'app/api/services';
import { sumList } from 'app/app.component';

/**
 * Import-existing dialog for Assembly-Level Process Steps.
 *
 * Lets the user pick a Category > Process > Column from the production
 * /manufacture-processes table and copies the resulting time-coded steps into
 * the BOM/Quote as new editable assembly process rows. The copy is one-shot:
 * later edits on the production side do NOT propagate.
 *
 * Production stores per-step times in MINUTES; this dialog converts to hours
 * before handing back, since BomAssemblyProcess.runPerUnitHours is in hours.
 */
export interface ImportManufactureProcessDialogData {
    // Optional: when the opener has the breakdown loaded (old builder, quote wizard)
    // it passes it in; otherwise the dialog fetches get-categories-breakdown itself
    // (the builder-core path, which had no reason to pre-load a whole lookup tree).
    manufactureProcesses?: ManufactureProcessCategoryBreakdownDto[];
    existingMaxSequence: number;
    // Live cost-centre list (code -> current rate). Used to resolve each imported
    // operation's labour rate by code rather than trusting the stage's frozen
    // CostCentreRate snapshot, which goes stale (and even disagrees with itself
    // for the same code) when cost-centre rates change after the process was set up.
    costCentres?: CostCentreDto[];
}

@Component({
    selector: 'app-import-manufacture-process-dialog',
    templateUrl: './import-manufacture-process-dialog.component.html',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatOptionModule,
        MatButtonModule,
        MatIconModule,
        CurrencyPipe,
        DecimalPipe,
    ],
})
export class ImportManufactureProcessDialogComponent implements OnInit {
    public categories: ManufactureProcessCategoryBreakdownDto[] = [];

    public form: FormGroup;

    public loadedProcess: ManufactureProcessDto | null = null;
    public loading = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ImportManufactureProcessDialogData,
        public dialogRef: MatDialogRef<ImportManufactureProcessDialogComponent>,
        private manufactureProcessService: ManufactureProcessService,
        private fb: FormBuilder,
    ) {
        this.form = this.fb.group({
            categoryId: [null],
            processId: [{ value: null, disabled: true }],
            columnDefinitionId: [{ value: null, disabled: true }],
        });
    }

    ngOnInit(): void {
        this.categories = this.data?.manufactureProcesses ?? [];
        // Self-serve when the opener didn't pre-load the breakdown (builder-core path).
        if (this.categories.length === 0) {
            this.loading = true;
            this.manufactureProcessService.apiManufactureProcessGetCategoriesBreakdownGet$Json()
                .subscribe({
                    next: res => {
                        this.categories = res?.data ?? [];
                        this.loading = false;
                    },
                    error: () => { this.loading = false; },
                });
        }
    }

    get selectedCategoryId(): number | null { return this.form.controls['categoryId'].value; }
    get selectedProcessId(): number | null { return this.form.controls['processId'].value; }
    get selectedColumnDefinitionId(): number | null { return this.form.controls['columnDefinitionId'].value; }

    // Cascading getters — keep selection tree consistent and reset child levels on parent change.
    getProcesses(): ManufactureProcessBreakdownDto[] {
        return this.categories.find(c => c.manufactureProcessCategoryId === this.selectedCategoryId)?.processes ?? [];
    }

    getColumns(): ManufactureProcessColumnDefinitionDto[] {
        const cols = this.getProcesses().find(p => p.manufactureProcessId === this.selectedProcessId)?.columnDefinitions ?? [];
        // Only time-typed columns make sense here — non-time values aren't durations
        return cols.filter(c => c.isTimeColumn);
    }

    onCategoryChanged(): void {
        this.form.patchValue({ processId: null, columnDefinitionId: null }, { emitEvent: false });
        this.form.controls['processId'].enable({ emitEvent: false });
        this.form.controls['columnDefinitionId'].disable({ emitEvent: false });
        this.loadedProcess = null;
    }

    onProcessChanged(): void {
        this.form.patchValue({ columnDefinitionId: null }, { emitEvent: false });
        if (this.selectedProcessId) {
            this.form.controls['columnDefinitionId'].enable({ emitEvent: false });
        } else {
            this.form.controls['columnDefinitionId'].disable({ emitEvent: false });
        }
        this.loadedProcess = null;
    }

    onColumnChanged(): void {
        if (!this.selectedProcessId || !this.selectedColumnDefinitionId) {
            this.loadedProcess = null;
            return;
        }
        this.loadProcess();
    }

    // Pulls the full process tree (stages > steps > values) and resolves each step's
    // value for the chosen column. Mirrors the wizard's loadChosenManufactureProcess.
    loadProcess(): void {
        if (!this.selectedProcessId || !this.selectedColumnDefinitionId) return;

        this.loading = true;
        this.manufactureProcessService
            .apiManufactureProcessGetProcessGet$Json$Response({ processId: this.selectedProcessId })
            .subscribe({
                next: res => {
                    const process = res?.body?.data;
                    if (!process) { this.loading = false; return; }

                    for (const stage of (process.stages ?? [])) {
                        for (const step of (stage.steps ?? [])) {
                            (step as any).value = step.values?.find(v => v.columnDefinitionId === this.selectedColumnDefinitionId)?.value;
                        }
                    }
                    this.loadedProcess = process;
                    this.loading = false;
                },
                error: () => { this.loading = false; },
            });
    }

    // Flat preview rows, keyed for trackBy. Each step carries its parent
    // stage's cost-centre snapshot (set via the manufacture-processes editor —
    // Phase 2 of the cost-centre-per-stage feature) so the imported assembly
    // process inherits the right rate without the user having to re-pick.
    //
    // Memoised — without this, every CD pass rebuilt a fresh array of fresh
    // row objects, and the template's `@for ... track row` (identity-based)
    // tore down + recreated every row's view on every tick. That spammed
    // NG0100 (row.isHighlighted "undefined → false" — the *row reference*
    // was changing between dirty-check and stable-check) and left ghost rows
    // in the DOM. Invalidated whenever loadedProcess reference changes,
    // which is the only thing that can change the rows.
    private _previewRowsCache: Array<{ stage: string; step: string; minutes: number; isHighlighted: boolean; isCountedOnce: boolean; setupMinutes: number; costCentreCode: string; costCentreRate: number; stageDescription: string; swimlaneDefinitionId: number | null }> | null = null;
    private _previewRowsCacheSource: ManufactureProcessDto | null = null;

    getPreviewRows(): Array<{ stage: string; step: string; minutes: number; isHighlighted: boolean; isCountedOnce: boolean; setupMinutes: number; costCentreCode: string; costCentreRate: number; stageDescription: string; swimlaneDefinitionId: number | null }> {
        if (this._previewRowsCache && this._previewRowsCacheSource === this.loadedProcess) {
            return this._previewRowsCache;
        }
        if (!this.loadedProcess?.stages?.length) {
            this._previewRowsCache = [];
            this._previewRowsCacheSource = this.loadedProcess;
            return this._previewRowsCache;
        }
        const rows: Array<{ stage: string; step: string; minutes: number; isHighlighted: boolean; isCountedOnce: boolean; setupMinutes: number; costCentreCode: string; costCentreRate: number; stageDescription: string; swimlaneDefinitionId: number | null }> = [];
        for (const stage of this.loadedProcess.stages) {
            // Resolve the cost centre's CURRENT rate by code. The stage carries a
            // frozen CostCentreRate snapshot that is often stale (£0 if the rate was
            // set after the stage was configured), which is why imported operations
            // were quoting labour at £0. Falling back to the snapshot only when the
            // code matches no live cost centre (e.g. a deleted/renamed one).
            const liveRate = this.data?.costCentres?.find(c => c.code === stage.costCentreCode)?.rate;
            for (const step of (stage.steps ?? [])) {
                rows.push({
                    stage: stage.name ?? '',
                    step: step.name ?? '',
                    minutes: +((step as any).value ?? 0) || 0,
                    isHighlighted: !!step.isHighlighted,
                    isCountedOnce: !!step.isCountedOnce,
                    setupMinutes: +(step.setupMinutes ?? 0) || 0,
                    costCentreCode: stage.costCentreCode ?? '',
                    costCentreRate: liveRate ?? stage.costCentreRate ?? 0,
                    // Snapshot the stage's subtext + board lane onto every row so
                    // the WO can show them per stage (see entity comment).
                    stageDescription: stage.description ?? '',
                    swimlaneDefinitionId: stage.swimlaneDefinitionId ?? null,
                });
            }
        }
        this._previewRowsCache = rows;
        this._previewRowsCacheSource = this.loadedProcess;
        return this._previewRowsCache;
    }

    getTotalMinutes(): number {
        return sumList(this.getPreviewRows().map(r => r.minutes));
    }

    getStepCount(): number {
        return this.getPreviewRows().length;
    }

    canImport(): boolean {
        return !this.loading
            && !!this.loadedProcess
            && this.getPreviewRows().length > 0;
    }

    cancel(): void { this.dialogRef.close(null); }

    confirm(): void {
        if (!this.canImport()) return;

        const startSeq = (this.data?.existingMaxSequence ?? 0) + 10;
        const rows = this.getPreviewRows();

        // Map each step to a fresh assembly-process row. Sequence increments by 10 so users
        // can later slot new steps between imported ones without re-numbering.
        // costCentreCode + costCentreRate are inherited from the parent stage so the
        // imported assembly process picks up the rate the user configured on the
        // manufacture process — not hardcoded empty/zero.
        const result = rows.map((r, i) => ({
            sequence: startSeq + (i * 10),
            operationCode: '',
            operationName: r.stage ? `${r.stage}: ${r.step}` : r.step,
            costCentreCode: r.costCentreCode,
            costCentreRate: r.costCentreRate,
            isSubcontracted: false,
            sourceType: null,
            subcontractorName: '',
            subcontractorHourlyRate: 0,
            // Production stores minutes; BomAssemblyProcess wants hours. A
            // "counted once" step (CNC setup, oven cure/cool) goes into
            // setupHours so the cost rollup adds it once per batch; a per-item
            // step goes into runPerUnitHours so it scales with quantity.
            // Plus the step's own setup time (DLM soft-launch item 17), also once per batch.
            setupHours: (r.setupMinutes + (r.isCountedOnce ? r.minutes : 0)) / 60,
            runPerUnitHours: r.isCountedOnce ? 0 : r.minutes / 60,
            notes: '',
            // Carry the source step's "waiting" / non-working flag so the WO
            // dialog renders that row with a yellow background (oven, curing,
            // etc.). Inherited from ManufactureProcessStep.IsHighlighted.
            isHighlighted: r.isHighlighted,
            // Stage subtext + board lane snapshot, carried so the WO can show
            // which lane each stage belongs to and its subtext.
            stageDescription: r.stageDescription,
            swimlaneDefinitionId: r.swimlaneDefinitionId,
            // Assembly-level — no per-line attachment
            bomItemId: null,
            clientLineId: null,
        }));

        this.dialogRef.close(result);
    }
}
